import React, { useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";

import { peripheralService } from "/app/src/services";

import { Search } from "/app/src/components/generic/components/search";
import { useTranslation } from "react-i18next";
import { Peripheral as PeripheralType } from "/app/src/models";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "/app/src/components/generic/tables/table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
import useQueryParams from "/app/src/hooks/useQueryParams";
/**
 * Component for displaying all peripherals
 */
export default function Peripherals() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sort, setSort] = useSortUpgrade([]);
  const [searchParams] = useSearchParams();

  const [searchString, setSearchString] = useState(
    searchParams.get("search") || "",
  );
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1") - 1,
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize") || "25"),
  );

  const columnHelper = createColumnHelper<PeripheralType>();
  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        cell: (info) => info.getValue(),
        header: () => t("translation:name"),
      }),
      columnHelper.accessor("typeDescription", {
        id: "typeDescription",
        cell: (info) => info.getValue(),
        header: () => t("translation:type"),
      }),
      columnHelper.accessor("description", {
        id: "description",
        cell: (info) => info.getValue(),
        header: () => t("translation:description"),
      }),
    ],
    [columnHelper, t],
  );

  const goToPeripheral = useCallback(
    (peripheralId: number) => {
      navigate(`/explorer/peripherals/${peripheralId}`);
    },
    [navigate],
  );

  const {
    data: peripherals,
    count: peripheralsCount,
    isFetching,
  } = usePaginatedData<PeripheralType>({
    queryKey: ["peripherals"],
    searchString,
    sort,
    page,
    pageSize,
    service: peripheralService,
  });

  const { updatePage, updatePageSize } = useQueryParams({
    count: peripheralsCount,
    isFetching,
    page,
    setPage,
    pageSize,
    setPageSize,
  });

  return (
    <div className="explorer">
      <Helmet>
        <title>{t("translation:peripherals")} - ItemPath</title>
      </Helmet>
      <Search allowClear setSearchString={setSearchString} />
      <Table
        loading={isFetching}
        rows={peripherals}
        tableColumns={columns}
        length={peripheralsCount}
        sort={sort}
        setSort={setSort}
        paginationEnabled={{
          currentPage: page,
          pageSize,
          setPage: updatePage,
          setPageSize: updatePageSize,
        }}
        enableRowSelection
        rowClicked={goToPeripheral}
        emptyText={t("translation:no_peripherals")}
      />
    </div>
  );
}
